import { ChevronDown } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import { useMe } from "@/hooks/data";
import { isPlatformAdmin } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/**
 * The "Technical details" pattern (ALP-84): raw JSON, IDs, provider names, trace links,
 * error codes. Customers never see it; Malfak staff (platform admins) get it collapsed
 * at the bottom of a card or sheet. Show the readable summary first, outside this box.
 *
 *   <TechnicalDetails>
 *     <pre>{JSON.stringify(run.trace, null, 2)}</pre>
 *   </TechnicalDetails>
 *
 * `staffOnly={false}` lets an admin-console page (already staff-only) reuse the look.
 */
export function TechnicalDetails({ children, className, staffOnly = true, defaultOpen = false }: { children: ReactNode; className?: string; staffOnly?: boolean; defaultOpen?: boolean }) {
  const { t } = useT();
  const me = useMe().data;
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  if (staffOnly && !isPlatformAdmin(me)) return null;
  return (
    <div className={cn("rounded-xl border border-dashed border-line", className)} data-testid="technical-details">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-start text-[12.5px] font-medium text-ink-2 hover:text-ink"
      >
        <span>{t("common.technical.title")}</span>
        <ChevronDown aria-hidden className={cn("size-4 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div id={id} role="region" aria-label={t("common.technical.title")} className="border-t border-dashed border-line px-3 py-2 text-[12px]">
          <p className="mb-2 text-ink-3">{t("common.technical.hint")}</p>
          {children}
        </div>
      )}
    </div>
  );
}
